import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomAudioPlayer from './custom_audio_player';
import { logPlayEvent, flushPlayEvents } from './listening_tracker';
import './css/persona.css';

let __ytApiLoadingPromise = null;
const loadYouTubeAPI = () => {
  if (typeof window !== 'undefined' && window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (__ytApiLoadingPromise) return __ytApiLoadingPromise;
  __ytApiLoadingPromise = new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
    document.head.appendChild(tag);
  });
  return __ytApiLoadingPromise;
};

const YouTubePlayer = ({ videoId, onEnded, onPlayerReady }) => {
  const containerRef = useRef(null);
  const instRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const YT = await loadYouTubeAPI();
      if (!mounted || !containerRef.current) return;
      const p = new YT.Player(containerRef.current, {
        width: '100%',
        height: '300',
        videoId: videoId || '',
        playerVars: { autoplay: 1, controls: 1, rel: 0, modestbranding: 1, playsinline: 1, origin: window.location.origin },
        events: {
          onReady: () => { try { onPlayerReady && onPlayerReady(p); } catch (e) {} },
          onStateChange: (e) => { if (e && e.data === 0 && typeof onEnded === 'function') onEnded(); },
        },
      });
      instRef.current = p;
    })();
    return () => { try { instRef.current && instRef.current.destroy && instRef.current.destroy(); } catch (e) {} };
  }, []);

  useEffect(() => {
    if (instRef.current && videoId) {
      try { instRef.current.loadVideoById(videoId); instRef.current.playVideo(); } catch (e) {}
    }
  }, [videoId]);

  return <div style={{ width: '100%', maxWidth: 900, margin: '8px auto' }}><div ref={containerRef} /></div>;
};

const server = axios.create({ baseURL: '/api' });

const MusicPlayerContext = createContext(null);

/** Special playlist name — membership means “do not play”. Matches iOS. */
export const SKIPPED_PLAYLIST_NAME = 'Skipped';

export function isSkippedPlaylist(playlist) {
  return String(playlist?.name || '').localeCompare(SKIPPED_PLAYLIST_NAME, undefined, {
    sensitivity: 'accent',
  }) === 0;
}

export function getSkippedPlaylist(playlists = []) {
  return playlists.find(isSkippedPlaylist) || null;
}

export function getSkippedTrackIds(playlists = []) {
  return new Set(getSkippedPlaylist(playlists)?.trackIds || []);
}

export function areAllTracksSkipped(trackIds = [], playlists = []) {
  if (!trackIds.length) return false;
  const skipped = getSkippedTrackIds(playlists);
  return trackIds.every((id) => skipped.has(id));
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) {
    throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  }
  return ctx;
}

function authHeaders() {
  const token = localStorage.getItem('walmates-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQueue(allTracks, addedTracks = []) {
  const added = addedTracks.filter(
    (t) => !allTracks.some((ft) => (ft.video_id || ft.id) === (t.video_id || t.id))
  );
  return [...allTracks, ...added];
}

function tracksForPlaylist(playlist, allTracks) {
  if (!playlist?.trackIds?.length) return [];
  return playlist.trackIds
    .map((videoId) => allTracks.find((t) => t.video_id === videoId))
    .filter(Boolean);
}

function coverTrackForPlaylist(playlist, allTracks) {
  return tracksForPlaylist(playlist, allTracks)[0] || null;
}

function trackIdOf(t) {
  return t?.video_id || t?.id || null;
}

function makeSourceId() {
  return `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export const PLAYING_SESSION_DETAIL_ID = '__playing_session__';

export function countMergedQueueTracks(sources = []) {
  const seen = new Set();
  let count = 0;
  for (const src of sources) {
    for (const id of src.trackIds || []) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      count += 1;
    }
  }
  return count;
}

export function mergeTracksFromSources(sources, allTracks, skippedIds = new Set()) {
  const seen = new Set();
  const merged = [];
  for (const src of sources) {
    for (const id of src.trackIds || []) {
      if (!id || seen.has(id) || skippedIds.has(id)) continue;
      const track = allTracks.find((t) => trackIdOf(t) === id);
      if (track) {
        seen.add(id);
        merged.push(track);
      }
    }
  }
  return merged;
}

function resolveTracksForSource(source, allTracks) {
  return (source?.trackIds || [])
    .map((id) => allTracks.find((t) => t.video_id === id))
    .filter(Boolean);
}

function sourceFromPlaylist(playlist, allTracks) {
  const trackIds = [...(playlist.trackIds || [])];
  const cover = coverTrackForPlaylist(playlist, allTracks);
  return {
    id: makeSourceId(),
    kind: 'playlist',
    refKey: `playlist:${playlist.id}`,
    playlistId: playlist.id,
    name: playlist.name,
    trackIds,
    coverUrl: cover?.thumbnail_medium || cover?.thumbnail_default || '',
    label: 'Playlist',
  };
}

function sourceFromBrowse(session) {
  const trackIds = (session.tracks || []).map(trackIdOf).filter(Boolean);
  const cover = session.tracks?.[0];
  const label = session.kind === 'language'
    ? 'Language'
    : session.kind === 'genre'
      ? 'Genre'
      : session.kind === 'artist'
        ? 'Artist'
        : 'Library';
  return {
    id: makeSourceId(),
    kind: 'browse',
    refKey: `browse:${session.kind}:${session.key}`,
    browseKind: session.kind,
    browseKey: session.key,
    name: session.name || session.key,
    trackIds,
    coverUrl: cover?.thumbnail_medium || cover?.thumbnail_default || '',
    label,
  };
}

function sourceFromSearch(tracks, query) {
  const trackIds = tracks.map(trackIdOf).filter(Boolean);
  const cover = tracks[0];
  return {
    id: makeSourceId(),
    kind: 'search',
    refKey: `search:${query}`,
    name: `"${query}"`,
    trackIds,
    coverUrl: cover?.thumbnail_medium || cover?.thumbnail_default || '',
    label: 'Search',
  };
}

const ADDED_SOURCE_REF = 'added:session';

function sourceFromAdded(trackIds, coverTrack) {
  return {
    id: makeSourceId(),
    kind: 'added',
    refKey: ADDED_SOURCE_REF,
    name: 'Added',
    trackIds: [...trackIds],
    coverUrl: coverTrack?.thumbnail_medium || coverTrack?.thumbnail_default || '',
    label: 'Added',
  };
}

function readSavedQueueState() {
  try {
    return JSON.parse(localStorage.getItem('musicQueueState') || '{}') || {};
  } catch {
    return {};
  }
}

function sourcesFromSavedState(saved) {
  if (Array.isArray(saved.queueSources) && saved.queueSources.length) {
    return saved.queueSources.map((s) => ({ ...s, id: s.id || makeSourceId() }));
  }
  if (saved.activePlaylistId != null) {
    return [{
      id: makeSourceId(),
      kind: 'playlist',
      refKey: `playlist:${saved.activePlaylistId}`,
      playlistId: saved.activePlaylistId,
      name: '',
      trackIds: [],
      coverUrl: '',
      label: 'Playlist',
    }];
  }
  return [];
}

function sourceToActivePlaylist(source, extraSources = 0, mergedTrackCount = null) {
  if (!source) return null;
  return {
    id: source.kind === 'playlist' ? source.playlistId : source.refKey,
    name: source.name,
    trackCount: mergedTrackCount ?? (source.trackIds?.length || 0),
    coverUrl: source.coverUrl || '',
    isSearch: source.kind === 'search',
    isBrowse: source.kind === 'browse',
    isPersona: false,
    browseKind: source.browseKind,
    browseKey: source.browseKey,
    label: source.label,
    sourceId: source.id,
    extraSources,
  };
}

export function MusicPlayerProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMusicRoute = location.pathname === '/music';

  const audioRef = useRef(null);
  const tracksWidgetRef = useRef(null);
  const ytShellRef = useRef(null);
  const ytPlaceholderRef = useRef(null);
  const floatStateRef = useRef(false);
  const rafRef = useRef(0);
  const cachedTracks = useRef({});
  const currentTrackToken = useRef(0);
  const shuffleHistoryRef = useRef([]);
  /** Track ids already played in the current shuffle cycle (no repeats until all playable heard). */
  const shufflePlayedRef = useRef(new Set());
  const handleNextTrackRef = useRef(() => {});
  const skippedIdsRef = useRef(new Set());
  /** Prevents double-advance while the current track is skipped and queue rebuilds */
  const skipAdvanceForIdRef = useRef(null);
  const pendingRestoreTrackIdRef = useRef(null);
  const didRestorePlaybackRef = useRef(false);
  const shouldRestorePlaybackRef = useRef(false);

  const [audioSrc] = useState('');
  const [allTracks, setAllTracks] = useState([]);
  const [tags] = useState([]);
  const [loadingTrackId, setLoadingTrackId] = useState(null);
  const [playTrack, setPlayTrack] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
  /** Ordered playback sources — playlists, browse filters, search. Tracks merge into queue. */
  const [queueSources, setQueueSources] = useState(() => {
    const saved = readSavedQueueState();
    const sources = sourcesFromSavedState(saved);
    if (sources.length) shouldRestorePlaybackRef.current = true;
    if (saved.currentTrackId) pendingRestoreTrackIdRef.current = saved.currentTrackId;
    return sources;
  });
  const queueSourcesRef = useRef(queueSources);
  const prevQueueSourcesLenRef = useRef(queueSources.length);
  const [queue, setQueue] = useState([]);
  const [addedTracks, setAddedTracks] = useState(() => {
    const saved = readSavedQueueState();
    return Array.isArray(saved.addedTracks) ? saved.addedTracks : [];
  });
  /** Tracks queued via "Play Next" — playback order only; does not rearrange library tiles */
  const [playNextQueue, setPlayNextQueue] = useState([]);
  /** How many natural successors to skip when previewing / advancing (non-shuffle) */
  const [upNextSkipCount, setUpNextSkipCount] = useState(0);
  /** Pre-picked next track while shuffle is on (stable until played or skipped) */
  const [shuffleUpNext, setShuffleUpNext] = useState(null);
  const [shuffleMode, setShuffleMode] = useState(
    () => localStorage.getItem('music-shuffle-mode') === 'true'
  );
  const [useYouTube, setUseYouTube] = useState(
    () => localStorage.getItem('music-youtube-mode') === 'true'
  );
  const [ytPlayer, setYtPlayer] = useState(null);
  const [ytMountEl, setYtMountEl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // ── Personality / recommendation state ─────────────────────────────────
  const [personas, setPersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  /** Personality currently driving playback (user-initiated only). */
  const [activePersonas, setActivePersonas] = useState([]);
  /** Server-built varied queue for the active personality. */
  const [personaTracks, setPersonaTracks] = useState([]);
  const [moodToast, setMoodToast] = useState('');

  /** {track, startedAtMs, source} for the play currently in progress. */
  const currentPlayRef = useRef(null);
  /** Why we are leaving the current track: 'ended' | 'skip' (set by handleNextTrack). */
  const advanceReasonRef = useRef(null);
  const consecutiveSkipsRef = useRef(0);
  const recentSkipIdsRef = useRef([]);
  const recentLikeIdsRef = useRef([]);
  const lastMoodShiftAtRef = useRef(0);
  const moodToastTimerRef = useRef(null);
  const personaRefillingRef = useRef(false);
  const refillPersonaQueueRef = useRef(() => {});
  const moodShiftRef = useRef(() => {});
  const playTrackRef = useRef(null);

  // Ref mirrors so telemetry helpers stay stable across renders.
  const useYouTubeRef = useRef(useYouTube);
  const shuffleModeRef = useRef(shuffleMode);
  const activePersonasRef = useRef(activePersonas);
  useEffect(() => { useYouTubeRef.current = useYouTube; }, [useYouTube]);
  useEffect(() => { playTrackRef.current = playTrack; }, [playTrack]);
  useEffect(() => { shuffleModeRef.current = shuffleMode; }, [shuffleMode]);
  useEffect(() => { activePersonasRef.current = activePersonas; }, [activePersonas]);

  const showMoodToast = useCallback((message) => {
    setMoodToast(message);
    if (moodToastTimerRef.current) clearTimeout(moodToastTimerRef.current);
    moodToastTimerRef.current = setTimeout(() => setMoodToast(''), 4000);
  }, []);

  /**
   * Close out the play that is ending and record what happened.
   * reason: 'ended' (finished naturally) | 'skip' (next pressed) |
   *         'switch' (user chose another track) | 'stop'
   * persona_id is set only while a personality is actively playing —
   * non-persona shuffle never attributes plays to a personality.
   */
  const finalizeCurrentPlay = useCallback((reason) => {
    const current = currentPlayRef.current;
    currentPlayRef.current = null;
    if (!current?.track?.video_id) return;

    let playedMs = Date.now() - current.startedAtMs;
    let durationMs = 0;
    const audio = audioRef.current;
    if (!useYouTubeRef.current && audio) {
      const fromAudio = Math.round((audio.currentTime || 0) * 1000);
      if (fromAudio > 0) playedMs = fromAudio;
      durationMs = Math.round((audio.duration || 0) * 1000) || 0;
    }
    const ratio = durationMs > 0 ? playedMs / durationMs : 0;
    const completed = reason === 'ended' || ratio >= 0.85;
    const skipped = !completed && (
      (reason === 'skip' && ratio < 0.7) ||
      (reason === 'switch' && playedMs < 30000)
    );

    logPlayEvent({
      video_id: current.track.video_id,
      source: current.source,
      played_ms: playedMs,
      duration_ms: durationMs,
      completed,
      skipped,
      persona_id: activePersonasRef.current[0]?.id ?? null,
    });

    if (skipped) {
      consecutiveSkipsRef.current += 1;
      recentSkipIdsRef.current = [...recentSkipIdsRef.current, current.track.video_id].slice(-6);
      // Mood layer: repeated skips during personality play → re-tune the queue.
      if (
        activePersonasRef.current.length &&
        consecutiveSkipsRef.current >= 2 &&
        Date.now() - lastMoodShiftAtRef.current > 45000
      ) {
        lastMoodShiftAtRef.current = Date.now();
        moodShiftRef.current();
      }
    } else if (completed || ratio >= 0.5) {
      consecutiveSkipsRef.current = 0;
      recentLikeIdsRef.current = [...recentLikeIdsRef.current, current.track.video_id].slice(-5);
    }
  }, []);

  // Finalize + flush whatever is playing when the tab goes away.
  useEffect(() => {
    const onPageHide = () => {
      finalizeCurrentPlay('stop');
      flushPlayEvents({ keepalive: true });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [finalizeCurrentPlay]);

  useEffect(() => {
    localStorage.setItem('music-shuffle-mode', String(shuffleMode));
  }, [shuffleMode]);

  useEffect(() => {
    localStorage.setItem('music-youtube-mode', String(useYouTube));
  }, [useYouTube]);

  useEffect(() => {
    if (!useYouTube || !isMusicRoute) return;
    const shell = ytShellRef.current;
    const ph = ytPlaceholderRef.current;
    if (!shell || !ph) return;

    const FLOAT_ON = -80;
    const FLOAT_OFF = 20;

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const rect = shell.getBoundingClientRect();
        const shouldFloat = rect.top < FLOAT_ON;
        const shouldDock = rect.top > FLOAT_OFF;
        const isFloating = floatStateRef.current;

        if (!isFloating && shouldFloat) {
          floatStateRef.current = true;
          shell.classList.add('yt--floating');
          ph.classList.add('yt--active');
        } else if (isFloating && shouldDock) {
          floatStateRef.current = false;
          shell.classList.remove('yt--floating');
          ph.classList.remove('yt--active');
        }
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [useYouTube, isMusicRoute]);

  const handleTrackInfoUpdated = useCallback((updated) => {
    if (!updated?.video_id) return;
    const mergeOne = (t) => (t.video_id === updated.video_id ? { ...t, ...updated } : t);
    setAllTracks((prev) => prev.map(mergeOne));
    setQueue((prev) => prev.map(mergeOne));
    setPlayNextQueue((prev) => prev.map(mergeOne));
    setShuffleUpNext((prev) => (prev?.video_id === updated.video_id ? { ...prev, ...updated } : prev));
    setPlayTrack((prev) => (prev?.video_id === updated.video_id ? { ...prev, ...updated } : prev));
  }, []);

  const ensureSkippedPlaylistExists = useCallback(async (currentPlaylists) => {
    const existing = getSkippedPlaylist(currentPlaylists);
    if (existing) return existing;

    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    if (!token || !user_id) return null;

    try {
      const timestamp = Math.floor(Date.now() / 1000);
      const res = await server.post('/music/playlist/create', {
        user_id,
        name: SKIPPED_PLAYLIST_NAME,
        last_modified: timestamp,
      }, { headers: authHeaders() });
      const created = {
        id: res.data.id,
        name: SKIPPED_PLAYLIST_NAME,
        trackIds: [],
        lastModified: res.data.last_modified ?? timestamp,
        synced: true,
      };
      setPlaylists((prev) => (getSkippedPlaylist(prev) ? prev : [...prev, created]));
      return created;
    } catch (err) {
      console.error('Failed to create Skipped playlist:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    if (!token || !user_id) return;

    Promise.all([
      server.get(`/music/getTracks?user_id=${user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      server.get(`/music/playlists?user_id=${user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      server.get(`/music/playlist_tracks?user_id=${user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(async ([tracksRes, playlistsRes, tracksMapRes]) => {
        setAllTracks(tracksRes.data);
        const mapping = tracksMapRes.data;
        const playlistsWithTracks = (playlistsRes.data || []).map((pl) => ({
          ...pl,
          trackIds: mapping[pl.id] || [],
        }));
        setPlaylists(playlistsWithTracks);
        setPlaylistsLoaded(true);
        await ensureSkippedPlaylistExists(playlistsWithTracks);
      })
      .catch((err) => {
        console.error(err);
        setPlaylistsLoaded(true);
      });
  }, [ensureSkippedPlaylistExists]);

  useEffect(() => {
    skippedIdsRef.current = getSkippedTrackIds(playlists);
  }, [playlists]);

  const isTrackSkipped = useCallback((trackOrId) => {
    const id = typeof trackOrId === 'string' ? trackOrId : trackIdOf(trackOrId);
    return !!id && skippedIdsRef.current.has(id);
  }, []);

  const playableTracks = useCallback((tracks) => (
    (tracks || []).filter((t) => !isTrackSkipped(t))
  ), [isTrackSkipped]);

  const modifyPlaylistTracks = useCallback(async (playlistId, add = [], remove = []) => {
    if (!playlistId || (!add.length && !remove.length)) return;
    const removeSet = new Set(remove);
    const addSet = new Set(add);
    setPlaylists((prev) => prev.map((p) => {
      if (p.id !== playlistId) return p;
      const existing = p.trackIds || [];
      const withoutRemoved = existing.filter((id) => !removeSet.has(id));
      const nextIds = [...withoutRemoved];
      addSet.forEach((id) => {
        if (!nextIds.includes(id)) nextIds.push(id);
      });
      return { ...p, trackIds: nextIds };
    }));
    try {
      await server.post('/music/playlist/modify', {
        playlist_id: playlistId,
        add,
        remove,
      }, { headers: authHeaders() });
    } catch (err) {
      console.error('Failed to modify playlist:', err);
    }
  }, []);

  const addTracksToSkipped = useCallback(async (trackIds) => {
    const ids = [...new Set((trackIds || []).filter(Boolean))];
    if (!ids.length) return;
    let skipped = getSkippedPlaylist(playlists);
    if (!skipped) {
      skipped = await ensureSkippedPlaylistExists(playlists);
    }
    if (!skipped) return;
    const existing = new Set(skipped.trackIds || []);
    const toAdd = ids.filter((id) => !existing.has(id));
    if (!toAdd.length) return;
    await modifyPlaylistTracks(skipped.id, toAdd, []);
  }, [playlists, ensureSkippedPlaylistExists, modifyPlaylistTracks]);

  const removeTracksFromSkipped = useCallback(async (trackIds) => {
    const ids = [...new Set((trackIds || []).filter(Boolean))];
    if (!ids.length) return;
    const skipped = getSkippedPlaylist(playlists);
    if (!skipped) return;
    const existing = new Set(skipped.trackIds || []);
    const toRemove = ids.filter((id) => existing.has(id));
    if (!toRemove.length) return;
    await modifyPlaylistTracks(skipped.id, [], toRemove);
  }, [playlists, modifyPlaylistTracks]);

  const clearSkippedTracks = useCallback(async () => {
    const skipped = getSkippedPlaylist(playlists);
    if (!skipped?.trackIds?.length) return;
    await modifyPlaylistTracks(skipped.id, [], [...skipped.trackIds]);
  }, [playlists, modifyPlaylistTracks]);

  const skipPlaylistTracks = useCallback(async (playlistId) => {
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist || isSkippedPlaylist(playlist)) return;
    await addTracksToSkipped(playlist.trackIds || []);
  }, [playlists, addTracksToSkipped]);

  const keepPlaylistTracks = useCallback(async (playlistId) => {
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist || isSkippedPlaylist(playlist)) return;
    await removeTracksFromSkipped(playlist.trackIds || []);
  }, [playlists, removeTracksFromSkipped]);

  useEffect(() => {
    queueSourcesRef.current = queueSources;
  }, [queueSources]);

  /** Keep playlist sources in sync when playlist membership changes. */
  useEffect(() => {
    if (!playlistsLoaded) return;
    setQueueSources((prev) => prev
      .map((src) => {
        if (src.kind !== 'playlist') return src;
        const pl = playlists.find((p) => p.id === src.playlistId);
        if (!pl || isSkippedPlaylist(pl)) return null;
        const cover = coverTrackForPlaylist(pl, allTracks);
        return {
          ...src,
          name: pl.name,
          trackIds: [...(pl.trackIds || [])],
          coverUrl: cover?.thumbnail_medium || cover?.thumbnail_default || src.coverUrl || '',
        };
      })
      .filter(Boolean));
  }, [playlists, allTracks, playlistsLoaded]);

  useEffect(() => {
    if (activePersonas.length && personaTracks.length) {
      const refreshed = personaTracks.map(
        (t) => allTracks.find((at) => trackIdOf(at) === trackIdOf(t)) || t
      );
      setQueue(refreshed);
      return;
    }
    if (queueSources.length) {
      setQueue(mergeTracksFromSources(queueSources, allTracks, skippedIdsRef.current));
      return;
    }
    setQueue(buildQueue(allTracks, addedTracks));
  }, [allTracks, addedTracks, queueSources, activePersonas, personaTracks]);

  useEffect(() => {
    localStorage.setItem('musicQueueState', JSON.stringify({
      addedTracks,
      queueSources: queueSources.map(({ id, kind, refKey, playlistId, browseKind, browseKey, name, trackIds, coverUrl, label }) => ({
        id, kind, refKey, playlistId, browseKind, browseKey, name, trackIds, coverUrl, label,
      })),
      currentTrackId: playTrack?.video_id || playTrack?.id || null,
    }));
  }, [addedTracks, queueSources, playTrack?.video_id, playTrack?.id]);

  const fetchPlayUrl = async (track) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    const params = new URLSearchParams({ user_id: String(user_id) });
    if (track.video_id) params.set('video_id', track.video_id);
    if (track.name) params.set('name', track.name);
    if (track.shared_preview) params.set('shared', '1');
    const res = await server.get(`/music/playTrack?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.data?.url) throw new Error('No playback URL from server');
    return res.data.url;
  };

  const preloadTrack = (nextTrack) => {
    if (nextTrack == null || cachedTracks.current[nextTrack.video_id]) return;
    fetchPlayUrl(nextTrack).then((url) => {
      cachedTracks.current[nextTrack.video_id] = url;
    }).catch(console.error);
  };

  const resetShuffleCycle = useCallback((preserveIds = []) => {
    shufflePlayedRef.current = new Set((preserveIds || []).filter(Boolean));
  }, []);

  /** Pick next shuffle track without repeating until every playable queue item has played. */
  const pickShuffleUpNextTrack = useCallback((excludeVideoIds = []) => {
    const playable = playableTracks(queue);
    if (!playable.length) return null;
    if (playable.length === 1) return playable[0];

    const excludes = new Set(excludeVideoIds.filter(Boolean));
    const played = shufflePlayedRef.current;
    let pool = playable.filter((t) => {
      const id = t.video_id;
      return id && !excludes.has(id) && !played.has(id);
    });
    if (!pool.length) {
      // Full cycle complete — start a new one, still avoid the track we are leaving.
      resetShuffleCycle([...excludes]);
      pool = playable.filter((t) => t.video_id && !excludes.has(t.video_id));
    }
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }, [queue, playableTracks, resetShuffleCycle]);

  const pickRandomQueueIndex = (excludeVideoId) => {
    const pick = pickShuffleUpNextTrack(excludeVideoId ? [excludeVideoId] : []);
    if (!pick) return -1;
    return queue.findIndex((t) => t.video_id === pick.video_id);
  };

  // Keep a stable shuffle up-next while a track is playing
  useEffect(() => {
    if (!shuffleMode || !playTrack || queue.length <= 1) {
      setShuffleUpNext((prev) => (prev ? null : prev));
      return;
    }
    const currentId = playTrack.video_id;
    setShuffleUpNext((prev) => {
      const stillValid = prev
        && prev.video_id !== currentId
        && !isTrackSkipped(prev)
        && !shufflePlayedRef.current.has(prev.video_id)
        && queue.some((t) => t.video_id === prev.video_id);
      if (stillValid) return prev;
      return pickShuffleUpNextTrack([currentId]) || null;
    });
  }, [shuffleMode, playTrack?.video_id, queue, pickShuffleUpNextTrack, isTrackSkipped]);

  // New queue source session → new shuffle cycle (first source added or all cleared)
  useEffect(() => {
    const len = queueSources.length;
    const prev = prevQueueSourcesLenRef.current;
    if ((prev === 0 && len > 0) || (prev > 0 && len === 0)) {
      resetShuffleCycle([]);
      setShuffleUpNext(null);
    }
    prevQueueSourcesLenRef.current = len;
  }, [queueSources.length, resetShuffleCycle]);

  const beginPlayTelemetry = useCallback((track, source) => {
    const reason = advanceReasonRef.current
      || (source === 'tap' || source === 'search' ? 'switch' : 'skip');
    advanceReasonRef.current = null;
    finalizeCurrentPlay(reason);
    currentPlayRef.current = { track, startedAtMs: Date.now(), source };
  }, [finalizeCurrentPlay]);

  const playAudio = useCallback(async (index, track, source = 'auto') => {
    if (!track) return;
    if (isTrackSkipped(track)) {
      // Never start a skipped track — advance if something is already playing
      if (playTrack && !isTrackSkipped(playTrack)) {
        handleNextTrackRef.current();
      }
      return;
    }
    if (useYouTube) {
      beginPlayTelemetry(track, source);
      setPlayTrack(track);
      setLoadingTrackId(track.video_id || track.id || null);
      setTimeout(() => setLoadingTrackId(null), 0);
      return;
    }
    if (loadingTrackId === track.video_id || playTrack?.video_id === track.video_id) return;
    beginPlayTelemetry(track, source);

    const thisToken = ++currentTrackToken.current;
    setLoadingTrackId(track.video_id);

    const startPlayback = (url) => {
      if (thisToken !== currentTrackToken.current) return;
      const audio = audioRef.current;
      if (!audio) return;
      audio.src = url;
      setPlayTrack(track);
      audio.load();
      const clearOnPlaying = () => {
        setLoadingTrackId(null);
        audio.removeEventListener('playing', clearOnPlaying);
        audio.removeEventListener('error', clearOnError);
        audio.removeEventListener('stalled', clearOnError);
        audio.removeEventListener('abort', clearOnError);
      };
      const clearOnError = () => {
        setLoadingTrackId(null);
        audio.removeEventListener('playing', clearOnPlaying);
        audio.removeEventListener('error', clearOnError);
        audio.removeEventListener('stalled', clearOnError);
        audio.removeEventListener('abort', clearOnError);
      };
      audio.addEventListener('playing', clearOnPlaying);
      audio.addEventListener('error', clearOnError);
      audio.addEventListener('stalled', clearOnError);
      audio.addEventListener('abort', clearOnError);
      audio.play().catch((err) => {
        clearOnError();
        if (err?.name === 'NotAllowedError') {
          setIsPlaying(false);
          return;
        }
        handleNextTrackRef.current();
      });
    };

    if (cachedTracks.current[track.video_id]) {
      startPlayback(cachedTracks.current[track.video_id]);
    } else {
      try {
        const url = await fetchPlayUrl(track);
        cachedTracks.current[track.video_id] = url;
        startPlayback(url);
      } catch (err) {
        console.error('Audio load error:', err);
        setLoadingTrackId(null);
      }
    }
  }, [useYouTube, loadingTrackId, playTrack, isTrackSkipped, beginPlayTelemetry]);

  useEffect(() => {
    if (didRestorePlaybackRef.current || !shouldRestorePlaybackRef.current) return;
    if (!queueSources.length || !allTracks.length || !queue.length) return;
    if (!useYouTube && !audioRef.current) return;

    const id = pendingRestoreTrackIdRef.current;
    const track = (id && (
      queue.find((t) => trackIdOf(t) === id) || allTracks.find((t) => trackIdOf(t) === id)
    )) || null;
    if (!track && !playlistsLoaded) return;

    const start = track || queue[0];
    if (!start || isTrackSkipped(start)) return;

    shouldRestorePlaybackRef.current = false;
    didRestorePlaybackRef.current = true;
    pendingRestoreTrackIdRef.current = null;
    playAudio(0, start, 'auto');
  }, [allTracks, queue, queueSources.length, playlistsLoaded, playAudio, isTrackSkipped, useYouTube]);

  const insertTrackNext = useCallback((track) => {
    if (!track || isTrackSkipped(track)) return;
    const id = trackIdOf(track);
    if (!id) return;
    setPlayNextQueue((prev) => {
      const filtered = prev.filter((t) => trackIdOf(t) !== id);
      return [track, ...filtered];
    });
    setUpNextSkipCount(0);
    preloadTrack(track);
  }, [isTrackSkipped]);

  useEffect(() => {
    const handler = (e) => {
      const track = e?.detail?.track;
      if (!track) return;
      insertTrackNext(track);
    };
    window.addEventListener('queue:playnext', handler);
    return () => window.removeEventListener('queue:playnext', handler);
  }, [insertTrackNext]);

  useEffect(() => {
    setUpNextSkipCount(0);
  }, [playTrack?.video_id]);

  const scrollQueueToCurrent = useCallback(() => {
    const id = playTrack?.video_id || playTrack?.id;
    if (!id) return;
    const scroll = () => tracksWidgetRef.current?.scrollToTrackById(id);
    if (!isMusicRoute) {
      navigate('/music');
      setTimeout(scroll, 150);
    } else {
      scroll();
    }
  }, [playTrack, isMusicRoute, navigate]);

  useEffect(() => {
    const onPlayYouTube = (e) => {
      const t = e?.detail?.track;
      if (!t) return;
      const normalized = {
        ...t,
        video_id: t.video_id || t.id,
        title: t.title || t.name || '',
        artist: t.artist || t.channelTitle || '',
        thumbnail_medium: t.thumbnail_medium || t.thumbnail_high || t.thumbnail_default || t.thumbnail_url || '',
      };
      setUseYouTube(true);
      setPlayTrack(normalized);
      setLoadingTrackId(normalized.video_id || null);
      setTimeout(() => setLoadingTrackId(null), 0);
      setAddedTracks((prev) =>
        prev.some((x) => (x.video_id || x.id) === normalized.video_id)
          ? prev
          : [normalized, ...prev]
      );
      setTimeout(() => {
        try { scrollQueueToCurrent(); } catch (_) {}
      }, 150);
    };
    window.addEventListener('player:playYouTube', onPlayYouTube);
    return () => window.removeEventListener('player:playYouTube', onPlayYouTube);
  }, [scrollQueueToCurrent]);

  const handleNextTrack = useCallback((reason = 'skip') => {
    advanceReasonRef.current = reason === 'ended' ? 'ended' : 'skip';
    const autoSource = activePersonas.length ? 'persona' : shuffleMode ? 'shuffle' : 'auto';

    if (playNextQueue.length) {
      const nextPlayableIdx = playNextQueue.findIndex((t) => !isTrackSkipped(t));
      if (nextPlayableIdx < 0) {
        setPlayNextQueue([]);
      } else {
        const next = playNextQueue[nextPlayableIdx];
        setPlayNextQueue((prev) => prev.slice(nextPlayableIdx + 1).filter((t) => !isTrackSkipped(t)));
        setUpNextSkipCount(0);
        if (next) {
          const idx = queue.findIndex((t) => trackIdOf(t) === trackIdOf(next));
          playAudio(idx >= 0 ? idx : 0, next, autoSource);
        }
        return;
      }
    }

    const playable = playableTracks(queue);
    if (!playable.length) return;
    const currentId = playTrack?.video_id;
    const currentIndex = queue.findIndex((t) => t.video_id === currentId);

    if (shuffleMode) {
      if (currentId) {
        const hist = shuffleHistoryRef.current;
        if (hist[hist.length - 1] !== currentId) {
          shuffleHistoryRef.current = [...hist, currentId].slice(-200);
        }
        shufflePlayedRef.current.add(currentId);
      }
      const committed = shuffleUpNext
        && shuffleUpNext.video_id !== currentId
        && !isTrackSkipped(shuffleUpNext)
        && !shufflePlayedRef.current.has(shuffleUpNext.video_id)
        ? shuffleUpNext
        : null;
      setShuffleUpNext(null);
      setUpNextSkipCount(0);
      if (committed) {
        const committedIndex = queue.findIndex((t) => t.video_id === committed.video_id);
        playAudio(committedIndex >= 0 ? committedIndex : 0, committed, autoSource);
        return;
      }
      const nextIndex = pickRandomQueueIndex(currentId);
      if (nextIndex < 0) return;
      playAudio(nextIndex, queue[nextIndex], autoSource);
      return;
    }

    // Persona play: top up the smart queue before it runs out.
    if (activePersonas.length && currentIndex >= queue.length - 4) {
      refillPersonaQueueRef.current();
    }

    // Walk forward past skipped tracks (and up-next skip count)
    const skip = Math.max(0, upNextSkipCount);
    let steps = 1 + skip;
    let nextIndex = currentIndex >= 0 ? currentIndex : -1;
    let guard = 0;
    while (steps > 0 && guard < queue.length) {
      nextIndex = (nextIndex + 1) % queue.length;
      if (!isTrackSkipped(queue[nextIndex])) steps -= 1;
      guard += 1;
    }
    setUpNextSkipCount(0);
    if (nextIndex < 0 || isTrackSkipped(queue[nextIndex])) {
      const first = playable[0];
      const idx = queue.findIndex((t) => t.video_id === first.video_id);
      if (idx >= 0) playAudio(idx, first, autoSource);
      return;
    }
    playAudio(nextIndex, queue[nextIndex], autoSource);
  }, [queue, playTrack, shuffleMode, playAudio, playNextQueue, upNextSkipCount, shuffleUpNext, isTrackSkipped, playableTracks, activePersonas]);

  const handlePreviousTrack = useCallback(() => {
    const playable = playableTracks(queue);
    if (!playable.length) return;

    if (shuffleMode) {
      const hist = shuffleHistoryRef.current;
      while (hist.length > 0) {
        const prevId = hist[hist.length - 1];
        shuffleHistoryRef.current = hist.slice(0, -1);
        if (isTrackSkipped(prevId)) continue;
        const prevIndex = queue.findIndex((t) => t.video_id === prevId);
        if (prevIndex >= 0) {
          playAudio(prevIndex, queue[prevIndex], 'tap');
          return;
        }
      }
      const nextIndex = pickRandomQueueIndex(playTrack?.video_id);
      if (nextIndex < 0) return;
      playAudio(nextIndex, queue[nextIndex], 'shuffle');
      return;
    }

    const currentIndex = queue.findIndex((t) => t.video_id === playTrack?.video_id);
    let prevIndex = currentIndex >= 0 ? currentIndex : 0;
    let guard = 0;
    do {
      prevIndex = (prevIndex - 1 + queue.length) % queue.length;
      guard += 1;
    } while (isTrackSkipped(queue[prevIndex]) && guard < queue.length);
    if (isTrackSkipped(queue[prevIndex])) return;
    playAudio(prevIndex, queue[prevIndex], 'tap');
  }, [queue, playTrack, shuffleMode, playAudio, isTrackSkipped, playableTracks]);

  useEffect(() => {
    handleNextTrackRef.current = handleNextTrack;
  }, [handleNextTrack]);

  useEffect(() => {
    window.__playNextTrack = () => handleNextTrack('skip');
    window.__playPreviousTrack = handlePreviousTrack;
  }, [handleNextTrack, handlePreviousTrack]);

  const handleShuffle = useCallback(() => {
    if (shuffleMode) {
      setShuffleMode(false);
      setShuffleUpNext(null);
      return;
    }
    // Turning shuffle on: start a fresh cycle and immediately play a new track
    // from the current queue (library, or the active list — never replace a list).
    // Explicit shuffle overrides personality play (back to regular random).
    if (activePersonasRef.current.length) {
      setActivePersonas([]);
      setPersonaTracks([]);
    }
    const currentId = playTrack?.video_id;
    resetShuffleCycle(currentId ? [currentId] : []);
    shuffleHistoryRef.current = [];
    setPlayNextQueue([]);
    setUpNextSkipCount(0);
    setShuffleUpNext(null);
    setShuffleMode(true);
    const pick = pickShuffleUpNextTrack(currentId ? [currentId] : []);
    if (pick) {
      if (currentId) shufflePlayedRef.current.add(currentId);
      const idx = queue.findIndex((t) => t.video_id === pick.video_id);
      playAudio(idx >= 0 ? idx : 0, pick);
    }
  }, [
    shuffleMode,
    playTrack?.video_id,
    queue,
    pickShuffleUpNextTrack,
    playAudio,
    resetShuffleCycle,
  ]);

  const togglePlay = useCallback(() => {
    if (useYouTube) {
      try {
        if (!ytPlayer) return;
        if (ytPlayer.getPlayerState && ytPlayer.getPlayerState() === 1) {
          ytPlayer.pauseVideo?.();
        } else {
          ytPlayer.playVideo?.();
        }
      } catch (e) {}
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [useYouTube, ytPlayer]);

  const handlePlayingChange = useCallback((playing) => {
    setIsPlaying(!!playing);
  }, []);

  const hasActiveList = useCallback(() => (
    queueSourcesRef.current.length > 0 || activePersonasRef.current.length > 0
  ), []);

  const isTrackInCurrentQueue = useCallback((trackOrId) => {
    const id = typeof trackOrId === 'string' ? trackOrId : trackIdOf(trackOrId);
    if (!id) return false;
    if (activePersonasRef.current.length) {
      return personaTracks.some((t) => trackIdOf(t) === id);
    }
    return queueSourcesRef.current.some((s) => (s.trackIds || []).includes(id));
  }, [personaTracks]);

  const ensureTrackInCurrentList = useCallback((track) => {
    const id = trackIdOf(track);
    if (!id || isTrackSkipped(track)) return false;

    if (activePersonasRef.current.length) {
      setPersonaTracks((prev) => {
        if (prev.some((t) => trackIdOf(t) === id)) return prev;
        return [...prev, track];
      });
      return true;
    }

    const sources = queueSourcesRef.current;
    if (!sources.length) return false;
    if (sources.some((s) => (s.trackIds || []).includes(id))) return false;

    const existing = sources.find((s) => s.refKey === ADDED_SOURCE_REF);
    let next;
    if (existing) {
      next = sources.map((s) => (
        s.refKey === ADDED_SOURCE_REF
          ? { ...s, trackIds: [...(s.trackIds || []), id] }
          : s
      ));
    } else {
      next = [...sources, sourceFromAdded([id], track)];
    }
    queueSourcesRef.current = next;
    setQueueSources(next);
    return true;
  }, [isTrackSkipped]);

  const addTrackToCurrentList = useCallback((track) => {
    if (!track || isTrackSkipped(track)) return;
    if (!hasActiveList()) return;
    if (isTrackInCurrentQueue(track)) {
      showMoodToast('Already in the playing list');
      return;
    }
    if (ensureTrackInCurrentList(track)) {
      showMoodToast(`Added ${track.title || 'track'}`);
    }
  }, [hasActiveList, isTrackInCurrentQueue, ensureTrackInCurrentList, isTrackSkipped, showMoodToast]);

  const playTrackNow = useCallback((track) => {
    if (!track || isTrackSkipped(track)) return;
    if (hasActiveList()) {
      ensureTrackInCurrentList(track);
    }
    const idx = queue.findIndex((t) => trackIdOf(t) === trackIdOf(track));
    playAudio(idx >= 0 ? idx : 0, track, 'tap');
  }, [hasActiveList, ensureTrackInCurrentList, isTrackSkipped, queue, playAudio]);

  /** Play a single search hit in the current queue context — does not open a search playlist. */
  const handleSearchTrackClick = useCallback((index, track) => {
    if (isTrackSkipped(track)) return;
    if (hasActiveList()) {
      playTrackNow(track);
      return;
    }
    const idx = queue.findIndex((t) => trackIdOf(t) === trackIdOf(track));
    playAudio(idx >= 0 ? idx : index, track, 'search');
  }, [playAudio, isTrackSkipped, queue, hasActiveList, playTrackNow]);

  const handleTrackClick = useCallback((index, track) => {
    if (isTrackSkipped(track)) return;
    if (hasActiveList()) {
      playTrackNow(track);
      return;
    }
    const idx = queue.findIndex((t) => trackIdOf(t) === trackIdOf(track));
    playAudio(idx >= 0 ? idx : index, track, 'tap');
  }, [playAudio, isTrackSkipped, queue, hasActiveList, playTrackNow]);

  /** Preview an unowned global-catalog track without adding library membership. */
  const playSharedTrack = useCallback((track) => {
    if (!track) return;
    playAudio(0, { ...track, shared_preview: true }, 'search');
  }, [playAudio]);

  const appendQueueSource = useCallback((source) => {
    if (!source?.refKey) return false;
    const prev = queueSourcesRef.current;
    if (prev.some((s) => s.refKey === source.refKey)) return false;
    const next = [...prev, source];
    queueSourcesRef.current = next;
    setQueueSources(next);
    return true;
  }, []);

  const clearQueueSourceSession = useCallback(() => {
    setActivePersonas([]);
    setPersonaTracks([]);
  }, []);

  /** Play local search matches — appends as a queue source (or starts first source). */
  const playSearchResults = useCallback((tracks, query, options = {}) => {
    const { shuffle = false, startTrack = null } = options;
    const list = (Array.isArray(tracks) ? tracks : []).filter(Boolean);
    const playable = playableTracks(list);
    if (!playable.length) return;

    const q = String(query || '').trim();
    const isFirstSource = queueSourcesRef.current.length === 0;
    const source = sourceFromSearch(list, q);

    if (isFirstSource) {
      shuffleHistoryRef.current = [];
      resetShuffleCycle([]);
      setPlayNextQueue([]);
      setUpNextSkipCount(0);
      setShuffleUpNext(null);
      setShuffleMode(!!shuffle);
    }

    clearQueueSourceSession();
    if (!appendQueueSource(source)) return;

    if (isFirstSource) {
      let start = playable[0];
      const startId = trackIdOf(startTrack);
      if (startId && playable.some((t) => trackIdOf(t) === startId)) {
        start = playable.find((t) => trackIdOf(t) === startId) || startTrack;
      } else if (shuffle && playable.length > 1) {
        start = playable[Math.floor(Math.random() * playable.length)];
      }
      playAudio(0, start, 'tap');
    }
  }, [playableTracks, resetShuffleCycle, clearQueueSourceSession, appendQueueSource, playAudio]);

  const playPlaylist = useCallback((playlistId) => {
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist || isSkippedPlaylist(playlist)) return;
    const playlistTracks = tracksForPlaylist(playlist, allTracks);
    const playable = playableTracks(playlistTracks);
    if (!playable.length) return;

    const isFirstSource = queueSourcesRef.current.length === 0;
    const source = sourceFromPlaylist(playlist, allTracks);

    if (isFirstSource) {
      shuffleHistoryRef.current = [];
      resetShuffleCycle([]);
      setPlayNextQueue([]);
      setUpNextSkipCount(0);
      setShuffleUpNext(null);
    }

    clearQueueSourceSession();
    appendQueueSource(source);

    if (isFirstSource) {
      const first = playable[0];
      playAudio(0, first, 'playlist');
    }
  }, [playlists, allTracks, playAudio, playableTracks, resetShuffleCycle, clearQueueSourceSession, appendQueueSource]);

  /** Play a specific track while ensuring its playlist is in the queue. */
  const playTrackFromPlaylist = useCallback((playlistId, track) => {
    const playlist = playlists.find((p) => p.id === playlistId);
    if (!playlist || isSkippedPlaylist(playlist)) return;
    if (!track || isTrackSkipped(track)) return;
    const playlistTracks = tracksForPlaylist(playlist, allTracks);
    const trackId = trackIdOf(track);
    if (!playlistTracks.some((t) => trackIdOf(t) === trackId)) return;

    if (queueSourcesRef.current.length) {
      playTrackNow(track);
      return;
    }

    const refKey = `playlist:${playlistId}`;
    const hadSource = queueSourcesRef.current.some((s) => s.refKey === refKey);
    if (!hadSource) {
      clearQueueSourceSession();
      appendQueueSource(sourceFromPlaylist(playlist, allTracks));
    }
    playAudio(0, track, 'tap');
  }, [playlists, allTracks, playAudio, isTrackSkipped, clearQueueSourceSession, appendQueueSource, playTrackNow]);

  /** Play language / genre / artist — appends as a queue source. */
  const playBrowse = useCallback((session, options = {}) => {
    const { startTrack = null } = options;
    const list = (Array.isArray(session?.tracks) ? session.tracks : []).filter(Boolean);
    const playable = playableTracks(list);
    if (!playable.length || !session?.key) return;

    const isFirstSource = queueSourcesRef.current.length === 0;
    const source = sourceFromBrowse({ ...session, tracks: list });

    if (isFirstSource) {
      shuffleHistoryRef.current = [];
      resetShuffleCycle([]);
      setPlayNextQueue([]);
      setUpNextSkipCount(0);
      setShuffleUpNext(null);
    }

    clearQueueSourceSession();
    appendQueueSource(source);

    if (isFirstSource) {
      let start = playable[0];
      const startId = trackIdOf(startTrack);
      if (startId && playable.some((t) => trackIdOf(t) === startId)) {
        start = playable.find((t) => trackIdOf(t) === startId) || startTrack;
      }
      playAudio(0, start, 'browse');
    } else if (startTrack && !isTrackSkipped(startTrack)) {
      playAudio(0, startTrack, 'browse');
    }
  }, [playAudio, playableTracks, resetShuffleCycle, clearQueueSourceSession, appendQueueSource, isTrackSkipped]);

  const playTrackFromBrowse = useCallback((session) => {
    if (queueSourcesRef.current.length && session?.startTrack) {
      playTrackNow(session.startTrack);
      return;
    }
    playBrowse(session, { startTrack: session?.startTrack || null });
  }, [playBrowse, playTrackNow]);

  const removeTrackFromPlaylist = useCallback(async (playlistId, trackId) => {
    if (!playlistId || !trackId) return;
    await modifyPlaylistTracks(playlistId, [], [trackId]);
  }, [modifyPlaylistTracks]);

  // If the current track becomes skipped, advance once (or stop)
  useEffect(() => {
    setPlayNextQueue((prev) => {
      const next = prev.filter((t) => !skippedIdsRef.current.has(trackIdOf(t)));
      return next.length === prev.length ? prev : next;
    });

    const currentId = trackIdOf(playTrack);
    if (!currentId || !skippedIdsRef.current.has(currentId)) {
      if (skipAdvanceForIdRef.current && currentId !== skipAdvanceForIdRef.current) {
        skipAdvanceForIdRef.current = null;
      }
      return;
    }
    if (skipAdvanceForIdRef.current === currentId) return;
    skipAdvanceForIdRef.current = currentId;

    const playable = queue.filter((t) => !skippedIdsRef.current.has(trackIdOf(t)));
    if (playable.length) {
      handleNextTrackRef.current();
      return;
    }
    setPlayTrack(null);
    setIsPlaying(false);
    const audio = audioRef.current;
    if (audio) {
      try { audio.pause(); audio.removeAttribute('src'); } catch (_) {}
    }
  }, [playlists, playTrack, queue]);

  const removeQueueSource = useCallback((sourceId) => {
    const sources = queueSourcesRef.current;
    const removed = sources.find((s) => s.id === sourceId);
    if (!removed) return;

    const removedIds = new Set(removed.trackIds || []);
    const currentId = trackIdOf(playTrack);
    const willLoseCurrent = currentId && removedIds.has(currentId);

    setQueueSources((prev) => {
      const next = prev.filter((s) => s.id !== sourceId);
      queueSourcesRef.current = next;
      return next;
    });

    if (willLoseCurrent) {
      setTimeout(() => handleNextTrackRef.current(), 0);
    }
  }, [playTrack]);

  const clearActivePlaylist = useCallback(() => {
    if (queueSourcesRef.current.length) {
      queueSourcesRef.current = [];
      setQueueSources([]);
      shuffleHistoryRef.current = [];
      resetShuffleCycle([]);
      setPlayNextQueue([]);
      setUpNextSkipCount(0);
      setShuffleUpNext(null);
      return;
    }
    setActivePersonas([]);
    setPersonaTracks([]);
    shuffleHistoryRef.current = [];
    resetShuffleCycle([]);
    setUpNextSkipCount(0);
    setShuffleUpNext(null);
    setQueue(buildQueue(allTracks, addedTracks));
  }, [allTracks, addedTracks, resetShuffleCycle]);

  // ── Personality play (user-initiated smart mix) ────────────────────────

  const loadPersonas = useCallback(async () => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    if (!token || !user_id) return;
    setPersonasLoading(true);
    try {
      const res = await server.get(`/music/personas?user_id=${user_id}`, { headers: authHeaders() });
      setPersonas(res.data?.personas || []);
    } catch (err) {
      console.error('Failed to load personalities:', err);
    } finally {
      setPersonasLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPersonas();
  }, [loadPersonas]);

  const fetchPersonaQueue = useCallback(async (ids, { count = 120, exclude = [] } = {}) => {
    const user_id = localStorage.getItem('walmates-user_id');
    const params = new URLSearchParams({
      user_id: String(user_id),
      ids: ids.join(','),
      count: String(count),
    });
    if (exclude.length) params.set('exclude', exclude.join(','));
    const res = await server.get(`/music/personas/queue?${params.toString()}`, {
      headers: authHeaders(),
    });
    return res.data?.tracks || [];
  }, []);

  /** Map server queue entries onto library track objects; drop skipped ones. */
  const resolveQueueTracks = useCallback((tracks) => (
    (tracks || [])
      .map((t) => allTracks.find((at) => trackIdOf(at) === trackIdOf(t)) || t)
      .filter((t) => trackIdOf(t) && !isTrackSkipped(t))
  ), [allTracks, isTrackSkipped]);

  /** Start personality play — user must initiate; no auto-detection. */
  const playPersonas = useCallback(async (personaList) => {
    const list = (personaList || []).filter(Boolean);
    if (!list.length) return;
    try {
      const raw = await fetchPersonaQueue(list.map((p) => p.id));
      const tracks = resolveQueueTracks(raw);
      if (!tracks.length) {
        showMoodToast('Add tracks to this personality first');
        return;
      }
      shuffleHistoryRef.current = [];
      resetShuffleCycle([]);
      setPlayNextQueue([]);
      setUpNextSkipCount(0);
      setShuffleUpNext(null);
      setQueueSources([]);
      setShuffleMode(false);
      consecutiveSkipsRef.current = 0;
      recentSkipIdsRef.current = [];
      recentLikeIdsRef.current = [];
      setActivePersonas(list);
      setPersonaTracks(tracks);
      setQueue(tracks);
      playAudio(0, tracks[0], 'persona');
    } catch (err) {
      console.error('Failed to start personality play:', err);
    }
  }, [fetchPersonaQueue, resolveQueueTracks, playAudio, resetShuffleCycle, showMoodToast]);

  const clearPersonaPlay = useCallback(() => {
    setActivePersonas([]);
    setPersonaTracks([]);
  }, []);

  const renamePersona = useCallback(async (personaId, name) => {
    const user_id = localStorage.getItem('walmates-user_id');
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    try {
      await server.post('/music/personas/rename', {
        user_id: Number(user_id),
        persona_id: personaId,
        name: trimmed,
      }, { headers: authHeaders() });
      const patch = (p) => (p.id === personaId ? { ...p, name: trimmed } : p);
      setPersonas((prev) => prev.map(patch));
      setActivePersonas((prev) => prev.map(patch));
    } catch (err) {
      console.error('Failed to rename personality:', err);
    }
  }, []);

  const createPersona = useCallback(async (name) => {
    const user_id = localStorage.getItem('walmates-user_id');
    const trimmed = String(name || '').trim();
    if (!trimmed) return null;
    try {
      const res = await server.post('/music/personas/create', {
        user_id: Number(user_id),
        name: trimmed,
      }, { headers: authHeaders() });
      setPersonas(res.data?.personas || []);
      return res.data?.persona || null;
    } catch (err) {
      console.error('Failed to create personality:', err);
      return null;
    }
  }, []);

  const deletePersona = useCallback(async (persona) => {
    if (!persona?.id) return;
    if (!window.confirm(`Delete personality “${persona.name}”?`)) return;
    const user_id = localStorage.getItem('walmates-user_id');
    try {
      const res = await server.post('/music/personas/delete', {
        user_id: Number(user_id),
        persona_id: persona.id,
      }, { headers: authHeaders() });
      setPersonas(res.data?.personas || []);
      if (activePersonasRef.current.some((p) => p.id === persona.id)) {
        setActivePersonas([]);
        setPersonaTracks([]);
      }
    } catch (err) {
      console.error('Failed to delete personality:', err);
    }
  }, []);

  const addTracksToPersona = useCallback(async (personaId, { video_ids = [], playlist_ids = [] } = {}) => {
    const user_id = localStorage.getItem('walmates-user_id');
    try {
      const res = await server.post('/music/personas/tracks/add', {
        user_id: Number(user_id),
        persona_id: personaId,
        video_ids,
        playlist_ids,
      }, { headers: authHeaders() });
      await loadPersonas();
      return res.data;
    } catch (err) {
      console.error('Failed to add tracks to personality:', err);
      throw err;
    }
  }, [loadPersonas]);

  const removeTrackFromPersona = useCallback(async (personaId, videoId) => {
    const user_id = localStorage.getItem('walmates-user_id');
    try {
      await server.post('/music/personas/tracks/remove', {
        user_id: Number(user_id),
        persona_id: personaId,
        video_ids: [videoId],
      }, { headers: authHeaders() });
      await loadPersonas();
    } catch (err) {
      console.error('Failed to remove track from personality:', err);
      throw err;
    }
  }, [loadPersonas]);

  const suggestTracksForPersona = useCallback(async (personaId) => {
    const user_id = localStorage.getItem('walmates-user_id');
    const res = await server.post('/music/personas/suggest', {
      user_id: Number(user_id),
      persona_id: personaId,
    }, { headers: authHeaders() });
    return res.data?.tracks || [];
  }, []);

  const getSimilarTracks = useCallback(async (track, count = 10) => {
    const title = String(track?.title || track?.name || '').trim();
    const artist = String(track?.artist || '').trim();
    if (!title) throw new Error('This track has no title');
    const res = await server.post('/music/similar', { title, artist, count }, { headers: authHeaders() });
    return res.data;
  }, []);

  const [similarRequest, setSimilarRequest] = useState(null);
  const similarPendingRef = useRef(false);
  const similarRequestIdRef = useRef(0);
  const showSimilarTracks = useCallback(async (selectedTrack, openPage = true) => {
    if (similarPendingRef.current || !selectedTrack) return;
    const seed = { title: selectedTrack.title || selectedTrack.name || '', artist: selectedTrack.artist || '' };
    if (openPage) navigate('/music/similar', { state: { from: location.pathname } });
    const requestId = ++similarRequestIdRef.current;
    similarPendingRef.current = true;
    setSimilarRequest({ seed, loading: true, error: '', response: null });
    try {
      const response = await getSimilarTracks(seed);
      if (similarRequestIdRef.current === requestId) {
        setSimilarRequest({ seed, loading: false, error: '', response });
      }
    } catch (err) {
      if (similarRequestIdRef.current === requestId) {
        setSimilarRequest({ seed, loading: false, error: err.response?.data?.error || err.message || 'Could not find similar tracks', response: null });
      }
    } finally {
      similarPendingRef.current = false;
    }
  }, [getSimilarTracks, navigate, location.pathname]);

  const retrySimilarTracks = useCallback(() => {
    if (similarRequest?.seed) showSimilarTracks(similarRequest.seed, false);
  }, [similarRequest, showSimilarTracks]);

  // Top up the smart queue before it runs out (called from handleNextTrack).
  useEffect(() => {
    refillPersonaQueueRef.current = async () => {
      if (personaRefillingRef.current) return;
      const active = activePersonasRef.current;
      if (!active.length) return;
      personaRefillingRef.current = true;
      try {
        const existingIds = personaTracks.map(trackIdOf).filter(Boolean);
        const raw = await fetchPersonaQueue(active.map((p) => p.id), {
          count: 80,
          exclude: existingIds.slice(-80),
        });
        const existing = new Set(existingIds);
        const fresh = resolveQueueTracks(raw).filter((t) => !existing.has(trackIdOf(t)));
        if (fresh.length) setPersonaTracks((prev) => [...prev, ...fresh]);
      } catch (err) {
        console.warn('Personality queue refill failed:', err);
      } finally {
        personaRefillingRef.current = false;
      }
    };
  }, [personaTracks, fetchPersonaQueue, resolveQueueTracks]);

  // Mood layer: repeated skips → ask the server to re-rank what's left,
  // gravitating toward recent likes and away from recent skips.
  useEffect(() => {
    moodShiftRef.current = async () => {
      const active = activePersonasRef.current;
      if (!active.length) return;
      try {
        const user_id = localStorage.getItem('walmates-user_id');
        const current = playTrackRef.current;
        const currentId = trackIdOf(current);
        const currentIndex = personaTracks.findIndex((t) => trackIdOf(t) === currentId);
        const alreadyPlayed = personaTracks
          .slice(0, currentIndex >= 0 ? currentIndex + 1 : 0)
          .map(trackIdOf)
          .filter(Boolean);
        const res = await server.post('/music/personas/requeue', {
          user_id: Number(user_id),
          ids: active.map((p) => p.id),
          seed_video_ids: recentLikeIdsRef.current.slice(-3),
          avoid_video_ids: recentSkipIdsRef.current.slice(-5),
          exclude: [...alreadyPlayed, currentId].filter(Boolean),
          count: 60,
        }, { headers: authHeaders() });
        let tracks = resolveQueueTracks(res.data?.tracks || []);
        if (!tracks.length) return;
        if (current) {
          tracks = [current, ...tracks.filter((t) => trackIdOf(t) !== currentId)];
        }
        consecutiveSkipsRef.current = 0;
        setPersonaTracks(tracks);
        showMoodToast('Shifting the mix…');
      } catch (err) {
        console.warn('Mood shift failed:', err);
      }
    };
  }, [personaTracks, resolveQueueTracks, showMoodToast]);

  const displayQueue = useMemo(() => (
    buildQueue(allTracks, addedTracks)
  ), [allTracks, addedTracks]);

  const activePlaylistIds = useMemo(
    () => new Set(
      queueSources.filter((s) => s.kind === 'playlist').map((s) => s.playlistId)
    ),
    [queueSources]
  );

  const activeBrowseKeys = useMemo(
    () => new Set(
      queueSources.filter((s) => s.kind === 'browse').map((s) => s.browseKey)
    ),
    [queueSources]
  );

  const activePlaylist = (() => {
    if (activePersonas.length) {
      const first = activePersonas[0];
      return {
        id: 'personas',
        name: activePersonas.map((p) => p.name).join(' + '),
        trackCount: personaTracks.length,
        coverUrl: first?.cover_url || '',
        emoji: first?.emoji || '🎧',
        isSearch: false,
        isPersona: true,
        label: activePersonas.length > 1 ? 'Personalities' : 'Personality',
      };
    }
    if (queueSources.length) {
      return sourceToActivePlaylist(
        queueSources[0],
        queueSources.length - 1,
        countMergedQueueTracks(queueSources),
      );
    }
    return null;
  })();

  const requestAddTrack = useCallback((track) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    if (!token || !user_id) return;

    const pendingTrack = { ...track, pending: true };
    setAllTracks((prev) => {
      if (!track.shared_preview) return [pendingTrack, ...prev];
      return [...prev, pendingTrack].sort((a, b) => (
        Number(b.timestamp || 0) - Number(a.timestamp || 0)
      ));
    });
    setAddedTracks((prev) => [track, ...prev]);

    return server.post(`/music/addTrack?user_id=${user_id}`, track, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((result) => {
      const confirmedTrack = result.data;
      setAllTracks((prev) => prev.map((t) => (
        t.video_id === track.video_id && t.pending ? confirmedTrack : t
      )).sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0)));
      return confirmedTrack;
    }).catch((err) => {
      setAllTracks((prev) => prev.filter((t) => !(t.video_id === track.video_id && t.pending)));
      setAddedTracks((prev) => prev.filter((t) => t.video_id !== track.video_id));
      console.error('Error in downloading:', track.title, err);
      throw err;
    });
  }, []);

  const handleDeleteTrack = useCallback((index, track) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    const videoId = track?.video_id;
    if (!videoId) return Promise.resolve();
    return server.post(`/music/deleteTrack?user_id=${user_id}&video_id=${videoId}`, null, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => {
      setAllTracks((prev) => prev.filter((t) => t.video_id !== videoId));
      setAddedTracks((prev) => prev.filter((t) => t.video_id !== videoId));
      setQueueSources((prev) => prev
        .map((src) => ({
          ...src,
          trackIds: (src.trackIds || []).filter((id) => id !== videoId),
        }))
        .filter((src) => src.trackIds?.length));
    }).catch(console.error);
  }, []);

  /** Delete every track in a local search result set (web search-list Delete). */
  const deleteSearchResults = useCallback(async (tracks) => {
    const list = (Array.isArray(tracks) ? tracks : []).filter((t) => t?.video_id);
    if (!list.length) return;
    await Promise.allSettled(list.map((track, i) => handleDeleteTrack(i, track)));
  }, [handleDeleteTrack]);

  const getNextTrack = useCallback(() => {
    if (playNextQueue.length) {
      const nextTrack = playNextQueue.find((t) => !isTrackSkipped(t));
      if (!nextTrack) return null;
      preloadTrack(nextTrack);
      return nextTrack;
    }
    const playable = playableTracks(queue);
    if (!playable.length || !playTrack) return null;
    if (shuffleMode) {
      if (
        !shuffleUpNext
        || shuffleUpNext.video_id === playTrack.video_id
        || isTrackSkipped(shuffleUpNext)
      ) return null;
      preloadTrack(shuffleUpNext);
      return shuffleUpNext;
    }
    const currentId = playTrack.video_id;
    const currentIndex = queue.findIndex((t) => t.video_id === currentId);
    if (currentIndex < 0) return null;
    if (playable.length === 1 && playable[0].video_id === currentId) return null;
    const skip = Math.max(0, upNextSkipCount);
    let steps = 1 + skip;
    let nextIndex = currentIndex;
    let guard = 0;
    while (steps > 0 && guard < queue.length) {
      nextIndex = (nextIndex + 1) % queue.length;
      if (!isTrackSkipped(queue[nextIndex])) steps -= 1;
      guard += 1;
    }
    const nextTrack = queue[nextIndex];
    if (!nextTrack || nextTrack.video_id === currentId || isTrackSkipped(nextTrack)) return null;
    preloadTrack(nextTrack);
    return nextTrack;
  }, [queue, playTrack, shuffleMode, playNextQueue, upNextSkipCount, shuffleUpNext, isTrackSkipped, playableTracks]);

  const skipUpNext = useCallback(() => {
    if (playNextQueue.length) {
      setPlayNextQueue((prev) => {
        const idx = prev.findIndex((t) => !isTrackSkipped(t));
        if (idx < 0) return [];
        return prev.slice(idx + 1);
      });
      return;
    }
    const playable = playableTracks(queue);
    if (playable.length <= 1) return;
    if (shuffleMode) {
      const next = pickShuffleUpNextTrack([
        playTrack?.video_id,
        shuffleUpNext?.video_id,
      ]);
      setShuffleUpNext(next);
      return;
    }
    setUpNextSkipCount((c) => Math.min(c + 1, playable.length - 2));
  }, [
    playNextQueue.length,
    shuffleMode,
    queue,
    playTrack?.video_id,
    shuffleUpNext?.video_id,
    pickShuffleUpNextTrack,
    isTrackSkipped,
    playableTracks,
  ]);

  const openNowPlayingSheet = useCallback((action) => {
    if (!playTrack) return;
    const open = () => tracksWidgetRef.current?.[action]?.(playTrack);
    if (!isMusicRoute) {
      navigate('/music');
      setTimeout(open, 150);
    } else {
      open();
    }
  }, [playTrack, isMusicRoute, navigate]);

  const registerTracksWidget = useCallback((ref) => {
    tracksWidgetRef.current = ref?.current ?? ref;
  }, []);

  const registerYtMount = useCallback((node) => {
    setYtMountEl(node || null);
  }, []);

  const value = {
    allTracks,
    tags,
    playlists,
    setPlaylists,
    queue,
    displayQueue,
    queueSources,
    playTrack,
    loadingTrackId,
    isPlaying,
    shuffleMode,
    useYouTube,
    setUseYouTube,
    activePlaylist,
    activePlaylistIds,
    activeBrowseKeys,
    playPlaylist,
    playBrowse,
    playTrackFromBrowse,
    playSearchResults,
    playTrackFromPlaylist,
    addTrackToCurrentList,
    playTrackNow,
    isTrackInCurrentQueue,
    removeTrackFromPlaylist,
    clearActivePlaylist,
    removeQueueSource,
    handleSearchTrackClick,
    handleTrackClick,
    playSharedTrack,
    handleDeleteTrack,
    deleteSearchResults,
    requestAddTrack,
    getSimilarTracks,
    similarRequest,
    retrySimilarTracks,
    handleTrackInfoUpdated,
    handleNextTrack,
    handleShuffle,
    togglePlay,
    isTrackSkipped,
    addTracksToSkipped,
    removeTracksFromSkipped,
    clearSkippedTracks,
    skipPlaylistTracks,
    keepPlaylistTracks,
    registerTracksWidget,
    registerYtMount,
    // Personality / recommendation system
    personas,
    personasLoading,
    activePersonas,
    playPersonas,
    clearPersonaPlay,
    renamePersona,
    createPersona,
    deletePersona,
    addTracksToPersona,
    removeTrackFromPersona,
    suggestTracksForPersona,
    refreshPersonas: loadPersonas,
  };

  const youtubeDock = useYouTube ? (
    <div
      className={`music-yt-dock${isMusicRoute ? '' : ' music-yt-dock--hidden'}`}
      aria-hidden={!isMusicRoute}
    >
      <div className="yt-player-shell" ref={ytShellRef}>
        <YouTubePlayer
          videoId={playTrack?.video_id}
          onEnded={() => handleNextTrack('ended')}
          onPlayerReady={(p) => setYtPlayer(p)}
        />
      </div>
      <div className="yt-player-placeholder" ref={ytPlaceholderRef} />
    </div>
  ) : null;

  return (
    <MusicPlayerContext.Provider value={value}>
      {/* Player must live inside .music-shell so dark design tokens apply */}
      <div className="music-shell">
        {children}
        {moodToast && (
          <div className="music-mood-toast" role="status">{moodToast}</div>
        )}
        {isMusicRoute && ytMountEl
          ? createPortal(youtubeDock, ytMountEl)
          : youtubeDock}
        <CustomAudioPlayer
          ref={audioRef}
          audioSrc={useYouTube ? '' : audioSrc}
          track={playTrack}
          nextTrack={getNextTrack()}
          onSkipUpNext={skipUpNext}
          onEnded={() => handleNextTrack('ended')}
          onNextTrack={() => handleNextTrack('skip')}
          onPrevTrack={handlePreviousTrack}
          onShuffle={handleShuffle}
          shuffleActive={shuffleMode}
          onInfoClick={scrollQueueToCurrent}
          onAddToPlaylist={() => openNowPlayingSheet('openPlaylistForTrack')}
          onPlayNext={() => insertTrackNext(playTrack)}
          onUpdateInfo={() => openNowPlayingSheet('openUpdateInfoForTrack')}
          onSimilar={showSimilarTracks}
          chromeVisible={isMusicRoute}
          onPlayingChange={handlePlayingChange}
          activePlaylist={activePlaylist}
          onClearPlaylist={clearActivePlaylist}
          useExternalControls={useYouTube}
          externalControls={{
            play: () => ytPlayer && ytPlayer.playVideo && ytPlayer.playVideo(),
            pause: () => ytPlayer && ytPlayer.pauseVideo && ytPlayer.pauseVideo(),
            seek: (s) => ytPlayer && ytPlayer.seekTo && ytPlayer.seekTo(s, true),
            getTime: () => (ytPlayer && ytPlayer.getCurrentTime ? ytPlayer.getCurrentTime() : 0),
            getDuration: () => (ytPlayer && ytPlayer.getDuration ? ytPlayer.getDuration() : 0),
            isPlaying: () => (ytPlayer && ytPlayer.getPlayerState ? ytPlayer.getPlayerState() === 1 : false),
          }}
        />
      </div>
    </MusicPlayerContext.Provider>
  );
}
